import { useEffect, useRef } from 'react';
import type { PersonaDefinition, PersonaId } from '../types/persona';
import { accountInitials } from './WorkspaceAccountContext';
import { useWorkspaceChromeHosts } from './WorkspaceChromeContext';
import { useThemePreference } from './ThemeToggle';
import { Icon } from './Icons';

/** Presentation of the authenticated identity, never a source of authority. */
export function StandardAccountMenu({ activeProduct, availableProducts, capacity, displayName, email,
  onProductChange, onSignOut, secureAccount, surface }: {
  activeProduct: PersonaDefinition; availableProducts: readonly PersonaDefinition[]; capacity: string;
  displayName: string; email?: string; onProductChange?: (product: PersonaId) => void;
  onSignOut(): void; secureAccount: boolean; surface: string;
}) {
  const hosts = useWorkspaceChromeHosts();
  const open = hosts?.activeMobileLayer === 'account';
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useThemePreference();
  const close = () => hosts?.closeMobileLayer('account');
  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) hosts?.closeMobileLayer('account');
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); hosts?.closeMobileLayer('account'); trigger.current?.focus();
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', dismiss); document.removeEventListener('keydown', escape); };
  }, [open, hosts?.closeMobileLayer]);
  const path = activeProduct.id === 'individual' ? '/personal-tax' : activeProduct.id === 'business' ? '/business' : '/practice';
  return <div className="am-account-menu" ref={root} onBlur={(event) => {
    if (open && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) close();
  }}>
    <button aria-label={`Account menu for ${displayName}`} aria-expanded={open} aria-controls="am-account-popover"
      aria-haspopup="dialog" className="am-account-menu__trigger" ref={trigger} type="button"
      onClick={() => open ? close() : hosts?.openMobileLayer('account')} title={displayName}>
      <span aria-hidden="true">{accountInitials(displayName)}</span>
    </button>
    {open && <div aria-label="Your account" className="am-account-menu__popover" id="am-account-popover"
      ref={panel} role="dialog" tabIndex={-1}>
      <div aria-label={`Signed in as ${displayName}, ${capacity}`} className="am-account-menu__identity" title={email}>
        <span aria-hidden="true" className="am-account-menu__avatar">{accountInitials(displayName)}</span>
        <div><strong>{displayName}</strong>{email && <span>{email}</span>}<small>{surface} · {capacity}</small></div>
      </div>
      <a className="am-account-menu__item" href={secureAccount ? `/account/settings?product=${activeProduct.id}` : `${path}/settings`} onClick={(event) => {
        if (hosts?.requestSettings?.()) event.preventDefault(); close();
      }}><Icon name="settings" />Settings</a>
      {secureAccount && <a className="am-account-menu__item" href={`/account/settings?product=${activeProduct.id}`}>
        <Icon name="users" />Security &amp; billing</a>}
      <fieldset className="am-account-menu__appearance"><legend>Appearance</legend>
        {(['system', 'light', 'dark'] as const).map((value) => <label key={value}>
          <input checked={theme === value} name="account-appearance" onChange={() => setTheme(value)} type="radio" value={value} />
          <span>{value === 'system' ? 'System' : value === 'light' ? 'Light' : 'Dark'}</span>
        </label>)}
      </fieldset>
      {availableProducts.length > 1 && onProductChange && <nav aria-label="Product experience" className="am-account-menu__products">
        <span>Available products</span>{availableProducts.map(product => <button aria-pressed={product.id === activeProduct.id}
          key={product.id} type="button" onClick={() => { onProductChange(product.id); close(); }}>{product.shortLabel}</button>)}
      </nav>}
      <button className="am-account-menu__item" onClick={onSignOut} type="button"><Icon name="log-out" />Sign out</button>
    </div>}
  </div>;
}
